import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, ArrowRight, CheckCircle2, MessageCircle
} from 'lucide-react';

/* ── Logos de clientes ─────────────────────────────────────── */
const ClientLogos = () => (
  <div className="border-t border-white/10 pt-8 mt-10">
    <p className="text-white/30 text-[11px] uppercase tracking-widest font-bold mb-6">
      Postos que já confiam na Lukos
    </p>
    <div className="flex flex-wrap items-center gap-8">
      {['Ipiranga', 'Shell', 'Raízen', 'Vibra', 'Petrobras'].map((name) => (
        <span key={name} className="text-white/50 hover:text-white font-black text-base tracking-tight transition-colors cursor-default">
          {name}
        </span>
      ))}
    </div>
  </div>
);

/* ── Benefícios ────────────────────────────────────────────── */
const benefits = [
  'Integração completa PDV + Conciliação + Fidelidade',
  'Suporte especializado em postos de combustível',
  'Implementação em menos de 7 dias úteis',
  'Sem fidelidade — cancele quando quiser',
];

/* ── Estilos reutilizáveis ─────────────────────────────────── */
const inputClass =
  'w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#690093] focus:border-transparent transition-all bg-white';

const selectClass =
  'w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#690093] focus:border-transparent transition-all bg-white appearance-none cursor-pointer';

const Label = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
  <label htmlFor={htmlFor} className="block text-slate-600 text-[13px] font-semibold mb-1.5">
    {children} <span className="text-[#690093]">*</span>
  </label>
);

/* ── Página Principal ──────────────────────────────────────── */
export default function ContatoPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1500);
  };

  return (
    <div className="min-h-screen relative overflow-hidden font-['Inter',sans-serif]">

      {/* ── Background foto ───────────────────────────────────── */}
      <div className="absolute inset-0 z-0">
        <img
          src="/lukos_conciliacao_woman_premium.png"
          alt="Lukos Tecnologia"
          className="w-full h-full object-cover object-center scale-105"
        />
        {/* Overlay preto intenso */}
        <div className="absolute inset-0 bg-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/40" />
      </div>

      {/* ── Navbar mínima ─────────────────────────────────────── */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-5">
        <Link to="/">
          <img src="/logobranco.svg" alt="Lukos" className="h-8 w-auto" />
        </Link>
        <Link to="/" className="text-white/50 hover:text-white text-sm font-medium transition-colors">
          ← Voltar ao site
        </Link>
      </nav>

      {/* ── Conteúdo principal ────────────────────────────────── */}
      <div className="relative z-10 min-h-[calc(100vh-72px)] flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

            {/* ── LADO ESQUERDO ─────────────────────────────────── */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              {/* Título melhorado */}
              <h1
                className="text-5xl md:text-[58px] font-black text-white leading-[1.06] tracking-tight mb-6"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '-0.03em' }}
              >
                Automatize seu posto.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white/80 to-white/40 font-light">
                  Escale seus resultados.
                </span>
              </h1>

              <p className="text-white/55 text-lg leading-relaxed mb-10 max-w-lg">
                Converse com um especialista Lukos e descubra como integrar PDV,
                conciliação bancária e fidelidade em uma única plataforma — sem complicações.
              </p>

              <div className="space-y-4 mb-10">
                {benefits.map((b, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2 size={17} className="text-white/70 shrink-0" />
                    <span className="text-white/65 text-sm font-medium">{b}</span>
                  </motion.div>
                ))}
              </div>

              {/* Contatos diretos */}
              <div className="flex flex-wrap gap-6 text-sm">
                <a
                  href="https://wa.me/5511978747233"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/50 hover:text-white transition-colors"
                >
                  <MessageCircle size={15} className="text-green-400" />
                  (11) 97874-7233
                </a>
                <a href="tel:+551148588429" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors">
                  <Phone size={15} className="text-white/40" />
                  (11) 4858-8429
                </a>
                <a href="mailto:suporte@lukos.com.br" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors">
                  <Mail size={15} className="text-white/40" />
                  suporte@lukos.com.br
                </a>
              </div>

              <ClientLogos />
            </motion.div>

            {/* ── LADO DIREITO — FORMULÁRIO ──────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <div className="bg-white rounded-3xl shadow-[0_40px_100px_rgba(0,0,0,0.4)] p-8 md:p-10">
                {!submitted ? (
                  <>
                    <div className="mb-8">
                      <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">
                        Fale com um especialista agora
                      </h2>
                      <p className="text-slate-400 text-sm">
                        Respondemos em até <strong className="text-slate-600">2 horas úteis</strong>.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">

                      {/* Nome + Celular */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="nome">Nome</Label>
                          <input id="nome" type="text" placeholder="João Silva" required className={inputClass} />
                        </div>
                        <div>
                          <Label htmlFor="celular">Celular</Label>
                          <input id="celular" type="tel" placeholder="(11) 99999-9999" required className={inputClass} />
                        </div>
                      </div>

                      {/* Empresa + Email */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="empresa">Nome do Posto / Empresa</Label>
                          <input id="empresa" type="text" placeholder="Posto Exemplo" required className={inputClass} />
                        </div>
                        <div>
                          <Label htmlFor="email">E-mail Corporativo</Label>
                          <input id="email" type="email" placeholder="joao@posto.com.br" required className={inputClass} />
                        </div>
                      </div>

                      {/* CNPJ */}
                      <div>
                        <Label htmlFor="cnpj">CNPJ</Label>
                        <input id="cnpj" type="text" placeholder="00.000.000/0001-00" required className={inputClass} />
                      </div>

                      {/* Quantos postos — select melhorado */}
                      <div>
                        <Label htmlFor="postos">Quantos postos você possui?</Label>
                        <div className="relative">
                          <select id="postos" required className={selectClass}>
                            <option value="" disabled selected>Selecione uma opção</option>
                            <option value="1">1 posto</option>
                            <option value="2-3">2 a 3 postos</option>
                            <option value="4-10">4 a 10 postos</option>
                            <option value="10+">Mais de 10 postos</option>
                          </select>
                          {/* Seta customizada */}
                          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Solução de interesse — select melhorado */}
                      <div>
                        <label htmlFor="solucao" className="block text-slate-600 text-[13px] font-semibold mb-1.5">
                          Qual solução mais te interessa?
                        </label>
                        <div className="relative">
                          <select id="solucao" className={selectClass}>
                            <option value="">Todas as soluções</option>
                            <option value="pdv">PDV e Frente de Caixa</option>
                            <option value="conciliacao">Conciliação Bancária</option>
                            <option value="fidelidade">Programa de Fidelidade</option>
                            <option value="luk">Luk IA</option>
                            <option value="pay">Lukos Pay</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Botão */}
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#690093] hover:bg-[#4f006e] text-white font-black py-4 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-900/30 flex items-center justify-center gap-3 text-[15px] disabled:opacity-70 disabled:cursor-not-allowed disabled:scale-100 mt-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>Quero implementar o Lukos <ArrowRight size={17} /></>
                        )}
                      </button>

                      <p className="text-[11px] text-slate-400 text-center leading-relaxed pt-1">
                        Ao enviar, você concorda com nossa{' '}
                        <a href="#" className="underline hover:text-purple-700 transition-colors">Política de Privacidade</a>.
                        Não fazemos spam.
                      </p>
                    </form>
                  </>
                ) : (
                  /* ── Sucesso ── */
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10"
                  >
                    <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 size={40} className="text-green-500" />
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">Recebemos seu contato!</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-8 max-w-xs mx-auto">
                      Um especialista Lukos vai entrar em contato em até <strong>2 horas úteis</strong>.
                    </p>
                    <a
                      href="https://wa.me/5511978747233?text=Ol%C3%A1%2C%20acabei%20de%20preencher%20o%20formul%C3%A1rio%20e%20gostaria%20de%20falar%20sobre%20a%20Lukos!"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-4 rounded-xl transition-all hover:scale-105 shadow-lg shadow-green-500/20"
                    >
                      <MessageCircle size={18} />
                      Falar agora pelo WhatsApp
                    </a>
                  </motion.div>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </div>
  );
}
